import React, { useEffect } from "react";
import i18n from "./locale/i18n";
import { I18nextProvider } from "react-i18next";
import { App as AntdApp, ConfigProvider, Flex, Spin, theme } from "antd";
import { AppRoute } from "./Routes";
import { CookiesProvider } from "react-cookie";
import { useAuthenticate } from "./hooks/useAuthenticate";
import { useThemeContext } from "./context/theme/context";

const { darkAlgorithm, defaultAlgorithm } = theme;

function App() {
  const { state } = useThemeContext();
  const { loading } = useAuthenticate();

  useEffect(() => {
    const fallbackSrc = "/favicon.ico";

    const onErrorCapture = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement)) return;

      // Avoid infinite loops if the fallback itself fails.
      if (target.dataset.fallbackApplied === "true") return;
      if (target.currentSrc === fallbackSrc || target.src.endsWith(fallbackSrc)) {
        target.dataset.fallbackApplied = "true";
        return;
      }

      target.dataset.fallbackApplied = "true";
      // If srcset is present, browsers may keep selecting broken candidates.
      target.removeAttribute("srcset");
      target.src = fallbackSrc;
    };

    window.addEventListener("error", onErrorCapture, true);
    return () => window.removeEventListener("error", onErrorCapture, true);
  }, []);

  if (loading) {
    return (
      <Flex justify="center" align="center">
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <CookiesProvider>
      <I18nextProvider i18n={i18n}>
        <ConfigProvider
          direction={i18n.dir()}
          theme={{
            token: {
              colorPrimary: "#FAB92B",
              colorSuccess: "#9FF431",
              colorWarning: "#F9D22D",
              colorError: "#F94E3D",
              colorLink: "#33AAF2",
              colorInfo: "#33AAF2",
              fontSize: 14, // don't work on light theme
            },
            algorithm: state.mode === "dark" ? darkAlgorithm : defaultAlgorithm,
          }}
        >
          <AntdApp>
            <AppRoute />
          </AntdApp>
        </ConfigProvider>
      </I18nextProvider>
    </CookiesProvider>
  );
}

export default App;
